/**
 * Aspect — Client-side auth (MVP)
 * --------------------------------------------------------------
 * Demo session store until server auth lands. Passwords are hashed with
 * a simple digest — not production-grade; replace with real API calls.
 */

const USERS_KEY = "aspect:portal:users";
const SESSION_KEY = "aspect:portal:session";
const RESET_KEY = "aspect:portal:reset-tokens";

function digest(text) {
  let h = 0;
  for (let i = 0; i < text.length; i++) h = (Math.imul(31, h) + text.charCodeAt(i)) | 0;
  return `h${(h >>> 0).toString(36)}`;
}

function readUsers() {
  try {
    const raw = localStorage.getItem(USERS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeUsers(users) {
  localStorage.setItem(USERS_KEY, JSON.stringify(users));
}

function readResets() {
  try {
    const raw = localStorage.getItem(RESET_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeResets(map) {
  localStorage.setItem(RESET_KEY, JSON.stringify(map));
}

/** @returns {{ email: string, name: string } | null} */
export function getSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s?.email) return null;
    return s;
  } catch {
    return null;
  }
}

export function setSession(user) {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify({ email: user.email, name: user.name }));
}

export function clearSession() {
  sessionStorage.removeItem(SESSION_KEY);
}

export function register({ name, email, password }) {
  const em = String(email ?? "")
    .trim()
    .toLowerCase();
  const pw = String(password ?? "");
  if (!em || !pw || pw.length < 8) {
    return { ok: false, error: "Use a valid email and password (8+ characters)." };
  }
  const users = readUsers();
  if (users[em]) return { ok: false, error: "An account with this email already exists." };
  users[em] = { name: String(name ?? "").trim() || em.split("@")[0], hash: digest(pw), created: Date.now() };
  writeUsers(users);
  setSession({ email: em, name: users[em].name });
  return { ok: true };
}

export function login({ email, password, remember }) {
  const em = String(email ?? "")
    .trim()
    .toLowerCase();
  const users = readUsers();
  const u = users[em];
  if (!u || u.hash !== digest(String(password ?? ""))) {
    return { ok: false, error: "Invalid email or password." };
  }
  setSession({ email: em, name: u.name });
  if (remember) localStorage.setItem("aspect:portal:last-email", em);
  return { ok: true };
}

export function requestPasswordReset(email) {
  const em = String(email ?? "")
    .trim()
    .toLowerCase();
  const users = readUsers();
  if (!users[em]) {
    // Don't leak whether account exists
    return { ok: true, token: null };
  }
  const token = `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  const resets = readResets();
  resets[token] = { email: em, exp: Date.now() + 3600_000 };
  writeResets(resets);
  return { ok: true, token };
}

export function resetPassword(token, password) {
  const pw = String(password ?? "");
  if (pw.length < 8) return { ok: false, error: "Password must be at least 8 characters." };
  const resets = readResets();
  const rec = resets[token];
  if (!rec || rec.exp < Date.now()) return { ok: false, error: "This reset link has expired. Request a new one." };
  const users = readUsers();
  if (!users[rec.email]) return { ok: false, error: "Account not found." };
  users[rec.email].hash = digest(pw);
  writeUsers(users);
  delete resets[token];
  writeResets(resets);
  setSession({ email: rec.email, name: users[rec.email].name });
  return { ok: true };
}

export function getLastEmail() {
  return localStorage.getItem("aspect:portal:last-email") ?? "";
}
